import { reactive, toRefs, watch, ref, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'vuex';
import { Close } from '@element-plus/icons-vue';
import { loanApi } from '@/apis/spot';
import { toFormat, times, truncateDecimal } from '@/utils/number';
import { formatLocalTime } from '@/utils/time';
import { loanPledge } from '@/config/sensors';
import { saPushEvent } from '@/utils/sensors';
import { accounts } from '@/config/loan';
import { noDmainUrl } from '@/utils/url';
import { replace } from '@/locales/i18n';
import KycDialog from './Kyc';
import { LOAN_ERROR_CODE } from '../../constant/errorCode';
import { errorToast, successToast, infoToast } from '../../hooks/useToast';
import { useLoopResult } from '../../hooks/useLoopResult';
export default {
  props: {
    visible: {
      type: Boolean,
      default: false,
      required: true
    },
    data: {
      type: Object,
      default() {
        return {};
      },
      required: true
    }
  },
  emits: ['update:visible', 'success'],
  components: {
    Close,
    KycDialog
  },
  setup(props, {
    emit
  }) {
    const {
      t
    } = useI18n({
      useScope: 'global'
    });
    const store = useStore();
    const userInfo = computed(() => store.state.loan.user);
    // 是否为统一账户并且为资金账户
    const isUFA = computed(() => store.getters['user/isUTA'] && store.getters['user/isFA']);
    // 是否展示 STETH 币种的文案提示
    const showStethTip = computed(() => props?.data?.pledgeToken?.toLocaleUpperCase() === 'STETH');
    const state = reactive({
      loading: false,
      time: Date.now()
    });
    const isAgreement = ref(false);

    // 用户校验
    const checkUser = ref({
      visible: false,
      content: '',
      code: 0,
      confirmText: ''
    });
    const methods = {
      // 校验用户
      checkUserAccount(type) {
        if (type === 'kyc') {
          // 完成基础认证
          checkUser.value = {
            visible: true,
            type,
            content: t('loan_kyc_tip'),
            confirmText: t('confirm')
          };
          return false;
        }
        if (type === 'account') {
          // 联系客服
          checkUser.value = {
            visible: true,
            type,
            content: t('loan_account_tip'),
            confirmText: t('contactOnline')
          };
          return false;
        }
        return true;
      },
      // 确认订单弹层埋点
      handleSensors(btnName = '') {
        const {
          loanToken,
          pledgeToken,
          limit
        } = props.data;
        saPushEvent(loanPledge.loan_dialog_btn, {
          page_name: loanPledge.home,
          plate_area: 'BorrowingPopUp',
          borrowing_token: loanToken,
          pledge_token: pledgeToken,
          loan_period: limit,
          button_name: btnName
        });
      },
      // 关闭借款订单弹层
      handleDialogCancel() {
        if (!state.loading) {
          emit('update:visible', false);
        }
        methods.handleSensors('Close');
      },
      // 确认借款
      async handleDialogConfirm() {
        methods.handleSensors('Confirm');
        if (state.loading) return;
        state.loading = true;
        const {
          loanToken,
          loanTokenName,
          loanamount,
          pledgeToken,
          pledgeamount,
          limit,
          loanPrecision,
          targetAccountType,
          accountType
        } = props.data;
        // 调取借款申请接口 执行loading
        const res = await loanApi.postConfirmLoan({
          loanToken,
          // 借款币种
          loanQty: loanamount,
          // 借款数量
          pledgeToken,
          // 质押币种
          pledgeQty: pledgeamount,
          // 质押数量
          loanPeriod: limit,
          // 期限（天）
          serialNo: String(Date.now()),
          // 唯一id
          targetAccountType,
          // 放款帐户类型
          accountType // 质押账户类型
        });
        // eslint-disable-next-line camelcase
        const {
          code,
          ext_info,
          message
        } = res;
        saPushEvent(loanPledge.loan_result, {
          page_name: loanPledge.home,
          result_code: code
        });

        // 如果是初始、处理中，就去调取记录查询接口
        if (res.success) {
          const {
            status,
            loanNo
          } = res.result;
          if (status === 'I' || status === 'P') {
            useLoopResult({
              type: 'loan',
              url: 'getLoanOrderList',
              params: {
                loanNo
              }
            }).then(finalResult => {
              state.loading = false;
              emit('update:visible', false);
              const {
                status: finalState
              } = finalResult;
              if (finalState === 'S') {
                successToast(t('loan_success_tip', {
                  num: toFormat(loanamount, loanPrecision),
                  token: loanTokenName
                }));
                emit('success');
                return;
              }
              if (finalState === 'F') {
                errorToast(t('loan_failed'));
                return;
              }
              if (finalState === 'P') {
                infoToast(t('loan_processing'));
              }
            });
          }
          return;
        }
        emit('update:visible', false);
        if (code === 100501 || code === 100502) {
          const type = code === 100501 ? 'kyc' : 'account';
          methods.checkUserAccount(type);
          return;
        }
        if (LOAN_ERROR_CODE[code]) {
          // eslint-disable-next-line camelcase
          errorToast(t(LOAN_ERROR_CODE[code], {
            num: ext_info?.num,
            coin: ext_info?.coin
          }));
          return;
        }
        errorToast(message);
      }
    };

    // 曝光埋点
    watch(() => props.visible, value => {
      if (value) {
        state.loading = false;
        saPushEvent(loanPledge.loan_dialog, {
          page_name: loanPledge.home,
          plate_area: 'BorrowingPopUp'
        });
      }
    });
    return {
      t,
      ...toRefs(state),
      ...methods,
      checkUser,
      userInfo,
      accounts,
      isUFA,
      times,
      toFormat,
      formatLocalTime,
      showStethTip,
      noDmainUrl,
      replace,
      isAgreement,
      truncateDecimal
    };
  }
};